import { useRef } from 'react';
import { useEffectOnce } from 'react-use';
import arrowRight from '~/assets/icons/arrow_right.svg';
import { CollectionImage, CurrencyIcon } from '~/components/collections';
import { ClothesImage } from '~/components/collections/ClothesImage';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { Currency } from '~/shared';
import { formatTime } from '../../utils';
import {
	StyledBottomContainer,
	StyledContainer,
	StyledDiscountContainer,
	StyledDiscountTimerContainer,
	StyledHoverShadow,
	StyledImageContainer,
	StyledMoreBlock,
	StyledOldPrice,
	StyledPrice,
	StyledPriceContainer,
	StyledShadow,
	StyledTagsContainer,
	StyledTimerContainer,
	StyledTopShadow,
} from './ShopItem.styled';

export interface IShopItemProps {
	type: 'vehicle' | 'clothes';
	id: string;
	name: string;
	price: number;
	oldPrice?: number;
	discount?: number;
	dateDiscountEnd?: number | null;
	dateEnd?: number | null;
	onClick?: () => void;
}

const ShopItem = ({
	type,
	id,
	name,
	price,
	oldPrice,
	discount,
	dateDiscountEnd,
	dateEnd,
	onClick,
}: IShopItemProps) => {
	const donateIcon = useWalletIcon(Currency.Donate, true);
	const discountTimeRef = useRef<HTMLDivElement | null>(null);
	const endTimeRef = useRef<HTMLDivElement | null>(null);

	const hasDiscount = !!discount;
	const hasDiscountTimer = hasDiscount && !!dateDiscountEnd;
	const hasEndTimer = !hasDiscountTimer && !!dateEnd;

	useEffectOnce(() => {
		const update = () => {
			if (dateDiscountEnd && discountTimeRef.current) {
				const { time, unit } = formatTime(dateDiscountEnd);
				discountTimeRef.current.textContent = unit ? `${time} ${unit}` : time;
			}
			if (dateEnd && endTimeRef.current) {
				const { time, unit } = formatTime(dateEnd);
				endTimeRef.current.textContent = unit ? `${time} ${unit}` : time;
			}
		};

		update();
		const interval = setInterval(update, 1000);
		return () => clearInterval(interval);
	});

	return (
		<StyledContainer $type={type} $pointerCursor={!!onClick} onClick={onClick}>
			<StyledTopShadow />
			<StyledImageContainer $type={type}>
				{type === 'vehicle' && (
					<CollectionImage collection={'vehicles'} id={id} size={96} />
				)}
				{type === 'clothes' && <ClothesImage id={id} size={140} />}
			</StyledImageContainer>
			<StyledShadow />
			<StyledHoverShadow />
			<StyledTagsContainer>
				{hasDiscountTimer ? (
					<StyledDiscountTimerContainer>
						<Text>{discount! * 100}%</Text>
						<div className={'divider'} />
						<Text ref={discountTimeRef} />
					</StyledDiscountTimerContainer>
				) : (
					<>
						{hasEndTimer && (
							<StyledTimerContainer>
								<Text ref={endTimeRef} />
							</StyledTimerContainer>
						)}
						{hasDiscount && (
							<StyledDiscountContainer>
								<Text>{discount! * 100}%</Text>
							</StyledDiscountContainer>
						)}
					</>
				)}
			</StyledTagsContainer>
			<StyledBottomContainer>
				<Text>{name}</Text>
				<StyledPriceContainer>
					<StyledPrice $isDonate>
						<CurrencyIcon icon={donateIcon} size="md" color="#FFBB00" />
						<Text>{price.toLocaleString('ru-RU')}</Text>
					</StyledPrice>
					{oldPrice != null && oldPrice > 0 && (
						<StyledOldPrice>
							<CurrencyIcon icon={donateIcon} size={10} />
							<Text>{oldPrice.toLocaleString('ru-RU')}</Text>
							<div className={'line'} />
						</StyledOldPrice>
					)}
				</StyledPriceContainer>
				<StyledMoreBlock>
					<Text>Подробнее</Text>
					<img src={arrowRight} />
				</StyledMoreBlock>
			</StyledBottomContainer>
		</StyledContainer>
	);
};

export { ShopItem };
